import { useState, useCallback } from 'react';

// Local storage keys - only these are allowed
const STORAGE_KEYS = {
  LAST_SPIN_DATE: 'psw_lastSpinDate',    // YYYY-MM-DD
  TODAY_SKILL_ID: 'psw_todaySkillId',     // string
  TODAY_CONTEXT: 'psw_todayContext',      // string
  TODAY_USED: 'psw_todayUsed'             // boolean
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Custom hook for Power Skills Wheel localStorage
export const usePowerSkillsStorage = () => {
  // Initialize state from localStorage
  const [lastSpinDate, setLastSpinDateState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.LAST_SPIN_DATE) || null;
    } catch {
      return null;
    }
  });

  const [todaySkillId, setTodaySkillIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.TODAY_SKILL_ID) || null;
    } catch {
      return null;
    }
  });

  const [todayContext, setTodayContextState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.TODAY_CONTEXT) || 'daily_life';
    } catch {
      return 'daily_life';
    }
  });

  const [todayUsed, setTodayUsedState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TODAY_USED);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Check if user has already spun today
  const hasSpunToday = useCallback(() => {
    const today = getTodayDate();
    return lastSpinDate === today;
  }, [lastSpinDate]);

  // Record a spin with the skill ID
  const recordSpin = useCallback((skillId) => {
    const today = getTodayDate();
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SPIN_DATE, today);
      localStorage.setItem(STORAGE_KEYS.TODAY_SKILL_ID, skillId);
      localStorage.setItem(STORAGE_KEYS.TODAY_USED, 'false');
      setLastSpinDateState(today);
      setTodaySkillIdState(skillId);
      setTodayUsedState(false);
    } catch (e) {
      console.error('Failed to save spin data:', e);
    }
  }, []);

  // Update context
  const updateContext = useCallback((context) => {
    try {
      localStorage.setItem(STORAGE_KEYS.TODAY_CONTEXT, context);
      setTodayContextState(context);
    } catch (e) {
      console.error('Failed to save context:', e);
    }
  }, []);

  // Mark skill as used
  const markAsUsed = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.TODAY_USED, 'true');
      setTodayUsedState(true);
    } catch (e) {
      console.error('Failed to mark as used:', e);
    }
  }, []);

  // Get the saved skill for today (returns null if no spin today)
  const getTodaySkill = useCallback(() => {
    if (!hasSpunToday()) {
      return null;
    }
    return todaySkillId;
  }, [hasSpunToday, todaySkillId]);

  return {
    // State
    lastSpinDate,
    todaySkillId,
    todayContext,
    todayUsed,
    
    // Computed
    hasSpunToday,
    getTodaySkill,
    
    // Actions
    recordSpin,
    updateContext,
    markAsUsed
  };
};

export { STORAGE_KEYS, getTodayDate };
